import { useEffect, useRef, useState } from "react";

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

/**
 * Anima um número de seu valor anterior até o novo valor.
 * Respeita `prefers-reduced-motion`.
 */
const useCountUp = (value: number, duration = 900): number => {
  const [displayValue, setDisplayValue] = useState(value);
  const fromRef = useRef(value);
  const frameRef = useRef<number>();

  useEffect(() => {
    if (prefersReducedMotion()) {
      fromRef.current = value;
      setDisplayValue(value);
      return undefined;
    }

    const from = fromRef.current;
    const delta = value - from;

    if (delta === 0) {
      setDisplayValue(value);
      return undefined;
    }

    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      setDisplayValue(from + delta * easeOutCubic(progress));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = value;
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      fromRef.current = value;
    };
  }, [value, duration]);

  return displayValue;
};

export default useCountUp;
